import { useState } from "react";
import { api, type CustomerMatch } from "../app/api";
import { useAsync } from "../app/useAsync";
import { StatusChip } from "../components/StatusChip";

function tone(status: string): "ok" | "warn" | "no" {
  if (status === "matched") return "ok";
  if (status === "needs_review") return "warn";
  return "no";
}

export function MatchReviewPage() {
  const [reloadKey, setReloadKey] = useState(0);
  const { data, error, loading } = useAsync(api.matches, [reloadKey]);
  const [target, setTarget] = useState("hiramrios26@gmail.com");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function evaluate(e: React.FormEvent) {
    e.preventDefault();
    const v = target.trim();
    if (!v) return;
    setBusy(true);
    setMsg(null);
    try {
      const params = v.includes("@") ? { email: v } : { id: v };
      const res = await api.evaluateMatch(params);
      setMsg(
        res.ok && res.match
          ? `Recorded: ${res.match.matchStatus} (confidence ${res.match.confidenceScore})`
          : res.error ?? "No result",
      );
      setReloadKey((k) => k + 1);
    } catch (err) {
      setMsg(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  const matches: CustomerMatch[] = data?.matches ?? [];

  return (
    <>
      <h1>Match Review</h1>
      <p className="page-sub">
        Recorded Sandlot→Shopify match assessments. Records only — no customer is
        migrated and no Shopify data is written.
      </p>

      <form onSubmit={evaluate} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <input
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          placeholder="Sandlot email or ID to evaluate…"
          style={{
            flex: 1,
            padding: "0.5rem 0.7rem",
            background: "var(--surface)",
            border: "1px solid var(--hair)",
            borderRadius: 8,
            color: "var(--ink)",
          }}
        />
        <button
          type="submit"
          disabled={busy}
          style={{
            padding: "0.5rem 1rem",
            background: "var(--accent)",
            color: "#12140f",
            border: "none",
            borderRadius: 8,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          {busy ? "Evaluating…" : "Evaluate + record"}
        </button>
      </form>
      {msg && <p className="muted" style={{ marginBottom: 16 }}>{msg}</p>}

      {loading && <p className="muted">Loading…</p>}
      {error && (
        <p className="muted">Unavailable ({error}).</p>
      )}
      {!loading && !error && matches.length === 0 && (
        <div className="empty">
          No match records yet. Evaluate a customer above to create one.
        </div>
      )}

      {matches.length > 0 && (
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>Sandlot ID</th>
                <th>Shopify ID</th>
                <th>Confidence</th>
                <th>Candidates</th>
                <th>Status</th>
                <th>Reasons</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((mm) => (
                <tr key={mm.id}>
                  <td className="muted"><code>{mm.sandlotCustomerId}</code></td>
                  <td className="muted">{mm.shopifyCustomerId ?? "—"}</td>
                  <td>{mm.confidenceScore}</td>
                  <td className="muted">{mm.candidateCount}</td>
                  <td><StatusChip tone={tone(mm.matchStatus)} label={mm.matchStatus} /></td>
                  <td className="muted">{mm.matchReasons ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
